import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { VoipWorkstationService } from '../../../voip/services/voip-workstation.service';
import { ReachService } from '../../services/reach.service';
import { UserService } from '../../services';
import { withUtcZone } from '../../alarms-api';

export interface PatientCall {
    source: 'voip' | 'reach';
    id: number;
    recording_id: string | null;
    date: string;
    direction: 'in' | 'out';
    outcome: 'answered' | 'no_answer' | 'busy' | 'in_progress';
    duration: number | null;
    phone_number: string | null;
    match_source: 'band' | 'phone' | 'contact' | null;
    alarm_id: number | null;
    agent_name: string | null;
    summary: string | null;
    transcript_text: string | null;
    transcript: any[] | null;
}

interface CallSegment {
    agent: boolean;
    speaker: string;
    text: string;
}

/**
 * Rozmowy pacjenta: nagrania ratowników (VoIP) i rozmowy agenta AI (Reach).
 * Tryb tabeli (zakładka „Rozmowy” w szczegółach użytkownika, po [imei]) albo kompaktowy
 * (opis alarmu, po [alarmId]). Backend wpuszcza tylko managera i jego opiekunów
 * z przywilejem voip / enableAiParamedic — przy 403 komponent zgłasza brak dostępu.
 */
@Component({
    selector: 'app-patient-calls',
    templateUrl: './patient-calls.component.html',
    styleUrls: ['./patient-calls.component.scss']
})
export class PatientCallsComponent implements OnChanges, OnDestroy {
    @Input() imei: string;
    @Input() alarmId: number;
    @Input() compact = false;
    /** null = brak dostępu; inaczej, które źródła widzi zalogowany */
    @Output() access = new EventEmitter<{ voip: boolean, reach: boolean } | null>();

    readonly pageSize = 20;
    calls: PatientCall[] = [];
    total = 0;
    loading = false;
    error = false;
    expandedKey: string = null;
    audioUrl: SafeUrl = null;
    audioLoading = false;
    audioError = false;

    private rawAudioUrl: string = null;
    private audioSub: Subscription;
    private segmentsCache: { [key: string]: CallSegment[] } = {};

    constructor(
        private voipService: VoipWorkstationService,
        private reachService: ReachService,
        private userService: UserService,
        private translate: TranslateService,
        private sanitizer: DomSanitizer
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.imei || changes.alarmId) {
            this.calls = [];
            this.total = 0;
            this.collapse();
            this.load(0);
        }
    }

    ngOnDestroy(): void {
        this.collapse();
    }

    get canLoadMore(): boolean {
        return !this.compact && !this.loading && this.calls.length < this.total;
    }

    load(offset: number): void {
        const request = this.alarmId
            ? this.voipService.getTranscriptionsByAlarm(this.alarmId)
            : (this.imei ? this.voipService.getTranscriptionsByImei(this.imei, this.pageSize, offset) : null);
        if (!request) {
            return;
        }
        this.loading = true;
        this.error = false;
        request.subscribe(
            res => {
                this.loading = false;
                const page: PatientCall[] = (res && res.data) || [];
                this.calls = offset > 0 ? this.calls.concat(page) : page;
                this.total = res && res.total != null ? res.total : this.calls.length;
                this.access.emit({ voip: !!(res && res.can_voip), reach: !!(res && res.can_reach) });
            },
            err => {
                this.loading = false;
                if (err && (err.status === 403 || err.status === 404)) {
                    this.access.emit(null);
                } else {
                    this.error = true;
                }
            }
        );
    }

    key(call: PatientCall): string {
        return call.source + ':' + call.id;
    }

    callDate(call: PatientCall): any {
        return withUtcZone(call.date);
    }

    formatDuration(seconds: number): string {
        if (seconds == null) {
            return '-';
        }
        const s = Math.max(0, Math.round(seconds));
        return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
    }

    outcomeKey(call: PatientCall): string {
        return 'PATIENT_CALLS.OUTCOME_' + (call.outcome || 'no_answer').toUpperCase();
    }

    matchKey(call: PatientCall): string {
        return call.match_source ? 'PATIENT_CALLS.MATCH_' + call.match_source.toUpperCase() : null;
    }

    /** Audio Reach udostępnia tylko endpoint managera. */
    hasAudio(call: PatientCall): boolean {
        return call.source === 'voip' ? !!call.recording_id : this.userService.getRole() === 3;
    }

    segments(call: PatientCall): CallSegment[] {
        const key = this.key(call);
        if (!this.segmentsCache[key]) {
            const raw = Array.isArray(call.transcript) ? call.transcript : [];
            this.segmentsCache[key] = call.source === 'reach'
                ? raw.filter(t => t && t.message).map(t => ({
                    agent: t.role === 'agent',
                    speaker: this.translate.instant(t.role === 'agent' ? 'PATIENT_CALLS.SPEAKER_AGENT' : 'PATIENT_CALLS.SPEAKER_PATIENT'),
                    text: String(t.message).trim()
                }))
                : raw.filter(s => s && (s.text || '').trim()).map(s => {
                    const operator = (s.speaker || '').toLowerCase().indexOf('operator') !== -1;
                    return {
                        agent: operator,
                        speaker: this.translate.instant(operator ? 'VOIP.SPEAKER_OPERATOR' : 'PATIENT_CALLS.SPEAKER_PATIENT'),
                        text: String(s.text).trim()
                    };
                });
        }
        return this.segmentsCache[key];
    }

    toggle(call: PatientCall): void {
        const key = this.key(call);
        if (this.expandedKey === key) {
            this.collapse();
            return;
        }
        this.collapse();
        this.expandedKey = key;
        if (!this.hasAudio(call)) {
            return;
        }
        this.audioLoading = true;
        const audio$ = call.source === 'voip'
            ? this.voipService.getRecordingAudio(call.recording_id)
            : this.reachService.getCallLogAudio(call.id);
        this.audioSub = audio$.subscribe(
            blob => {
                this.audioLoading = false;
                if (!blob || !blob.size) {
                    this.audioError = true;
                    return;
                }
                this.rawAudioUrl = URL.createObjectURL(blob);
                this.audioUrl = this.sanitizer.bypassSecurityTrustUrl(this.rawAudioUrl);
            },
            () => {
                this.audioLoading = false;
                this.audioError = true;
            }
        );
    }

    trackByKey = (_: number, call: PatientCall) => this.key(call);

    private collapse(): void {
        this.expandedKey = null;
        if (this.audioSub) {
            this.audioSub.unsubscribe();
            this.audioSub = null;
        }
        if (this.rawAudioUrl) {
            URL.revokeObjectURL(this.rawAudioUrl);
            this.rawAudioUrl = null;
        }
        this.audioUrl = null;
        this.audioLoading = false;
        this.audioError = false;
    }
}
